import { useAui } from "@assistant-ui/react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Center,
  Divider,
  Flex,
  Icon,
  Input,
  Menu,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { HiDotsVertical } from "react-icons/hi";
import {
  HiOutlineArrowDownTray,
  HiOutlineArrowPath,
  HiOutlineClipboard,
  HiOutlineClock,
  HiOutlineGlobeAlt,
  HiOutlineShare,
} from "react-icons/hi2";
import {
  TbLayoutSidebarLeftExpand,
  TbLayoutSidebarRightExpand,
} from "react-icons/tb";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import {
  Alert,
  Button,
  IconButton,
  MenuButton,
  useToast,
} from "../../../../components";
import { CopyableChart } from "../../../../components/ai-elements";
import {
  dateRangeCustom,
  dateRangeFromType,
} from "../../../../components/SelectDateRange/SelectDateRange";
import { copy } from "../../../../utils/clipboard";
import { formatDate } from "../../../../utils/datetime";
import {
  useCustomInsightsReportQuery,
  useDeleteCustomInsightsReportMutation,
  useUpdateCustomInsightsReportTitleMutation,
} from "../../../graphql";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import type {
  InsightsChartData,
  SerializableFilterState,
} from "./customInsightsChatUtils";
import { useCustomInsightsFiltersContext } from "./CustomInsightsFiltersProvider";
import { CustomInsightsFilterSummary } from "./CustomInsightsFilterSummary";
import { CustomInsightsReportPrettyMarkdown } from "./CustomInsightsReportPrettyMarkdown";
import { CustomInsightsReportShareModal } from "./CustomInsightsReportShareModal";

type SavedResult = {
  summary?: string;
  charts?: InsightsChartData[];
};

function buildDynamicDateRange(
  filters: SerializableFilterState
): ReturnType<typeof dateRangeFromType> | null {
  const rawDateRange = filters.dateRange;
  if (!rawDateRange) return null;

  if (rawDateRange.type && rawDateRange.type !== "custom") {
    return dateRangeFromType(rawDateRange.type);
  }

  const savedStart = new Date(rawDateRange.start);
  const savedEnd = new Date(rawDateRange.end);
  if (Number.isNaN(savedStart.valueOf()) || Number.isNaN(savedEnd.valueOf())) {
    return null;
  }

  const daySpan = Math.max(
    1,
    Math.round(
      (savedEnd.valueOf() - savedStart.valueOf()) / (1000 * 60 * 60 * 24)
    )
  );
  const nextEnd = new Date();
  const nextStart = new Date(nextEnd);
  nextStart.setDate(nextEnd.getDate() - daySpan);

  return dateRangeCustom(nextStart, nextEnd);
}

function buildReportMarkdown(
  title: string,
  charts: InsightsChartData[] | undefined,
  summary: string | undefined
): string {
  const sections: string[] = [`# ${title}`];

  if (charts?.length) {
    charts.forEach((chart) => {
      sections.push(`## ${chart.title}`);

      const { labels, series, values } = chart;

      if (series?.length) {
        const header = `| | ${series.map((s) => s.name).join(" | ")} |`;
        const sep = `|---|${series.map(() => "---").join("|")}|`;
        const rows = chart.labels.map(
          (label, i) =>
            `| ${label} | ${series.map((s) => s.values[i] ?? "").join(" | ")} |`
        );
        sections.push([header, sep, ...rows].join("\n"));
      } else if (labels?.length && values?.length) {
        const header = "| | Value |";
        const sep = "|---|---|";
        const rows = labels.map(
          (label, i) => `| ${label} | ${values[i] ?? ""} |`
        );
        sections.push([header, sep, ...rows].join("\n"));
      }
    });
  }

  if (summary) {
    sections.push(summary);
  }

  return sections.join("\n\n");
}

const CustomInsightsViewPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const toast = useToast();
  const aui = useAui();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isShareOpen,
    onOpen: onShareOpen,
    onClose: onShareClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [copying, setCopying] = useState(false);
  const { setAllFilters } = useCustomInsightsFiltersContext();
  const { newExploration, setFiltersOpen, setRerunReport } =
    useCustomInsightsChat();

  const { data, loading, error } = useCustomInsightsReportQuery({
    variables: { id: id ?? "" },
    skip: !id,
  });

  const [deleteReport, { loading: deleting }] =
    useDeleteCustomInsightsReportMutation();

  const [updateTitle] = useUpdateCustomInsightsReportTitleMutation();

  const report = data?.customInsightsReport;

  const parsedResult = useMemo((): SavedResult | null => {
    if (!report?.result) return null;
    try {
      return JSON.parse(report.result) as SavedResult;
    } catch {
      return { summary: report.result };
    }
  }, [report?.result]);

  const parsedFilters = useMemo((): SerializableFilterState | null => {
    if (!report?.filters) return null;
    try {
      return typeof report.filters === "string"
        ? (JSON.parse(report.filters) as SerializableFilterState)
        : (report.filters as SerializableFilterState);
    } catch {
      return null;
    }
  }, [report?.filters]);

  const isCreator = report?.creator?.id === currentUser.id;

  const buildFiltersForFreshRun = useCallback(() => {
    if (!parsedFilters) return null;

    const dateRange = buildDynamicDateRange(parsedFilters);
    if (!dateRange) return null;

    return {
      ...parsedFilters,
      dateRange,
    };
  }, [parsedFilters]);

  const handleNewExplorationFromReport = (): void => {
    if (!report) return;
    try {
      const filters = buildFiltersForFreshRun();
      if (!filters) throw new Error("Invalid filters");

      newExploration();
      setAllFilters(filters);
      setFiltersOpen(true);
      setRerunReport({
        id: report.id,
        title: report.title,
        prompt: report.prompt,
        filters: report.filters,
        summary: parsedResult?.summary ?? "",
        charts: (parsedResult?.charts as InsightsChartData[]) ?? [],
      });
      aui.thread().composer().setText("");

      navigate("/insights/custom");
    } catch {
      toast({
        title: "Failed to start exploration",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    try {
      await deleteReport({ variables: { id } });
      onDeleteClose();
      navigate("/insights/custom");
    } catch {
      toast({
        title: "Failed to delete report",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSaveTitle = async (newTitle: string): Promise<void> => {
    if (!id) return;
    try {
      await updateTitle({ variables: { id, title: newTitle } });
      toast({
        title: "Report title updated",
        status: "success",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Failed to update title",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleCopyToClipboard = useCallback(async () => {
    if (!report || !parsedResult) return;
    setCopying(true);
    try {
      const markdown = buildReportMarkdown(
        report.title,
        parsedResult.charts,
        parsedResult.summary
      );
      const html = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      );
      await copy({ plain: markdown, html });
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Failed to copy",
        status: "error",
        duration: 2000,
      });
    } finally {
      setCopying(false);
    }
  }, [report, parsedResult, toast]);

  if (loading) {
    return (
      <Center py="12">
        <Text color="gray.500">Loading report...</Text>
      </Center>
    );
  }

  if (error || !report) {
    return <Alert status="error" description="Report not found" reload />;
  }

  return (
    <Flex h="100%" overflow="hidden">
      {/* Main content panel */}
      <Box flex="1" overflow="auto" px="8" pt="6" minW={0} bg="white">
        <Flex
          alignItems="center"
          gap={2}
          mb="4"
          justifyContent={report.prompt ? "space-between" : "flex-end"}
        >
          <Box flex="1" minW={0} px="3" py="2" fontSize="sm">
            <Text fontWeight="medium" color="gray.500">
              Saved report
            </Text>
          </Box>
          <Flex alignItems="center" gap={2}>
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                icon={<HiDotsVertical size="16px" />}
                variant="ghost"
                size="sm"
                color="gray.900"
              />
              <MenuList>
                <MenuItem onClick={onShareOpen}>
                  <Box flex="1" ml="2">
                    Share
                  </Box>
                </MenuItem>
                <MenuItem value="rerun-report" isDisabled>
                  <Box flex="1" ml="2">
                    Rerun report
                  </Box>
                </MenuItem>
                <MenuItem
                  value="new-exploration"
                  onClick={handleNewExplorationFromReport}
                >
                  <Box flex="1" ml="2">
                    New exploration from this report
                  </Box>
                </MenuItem>
              </MenuList>
            </Menu>
            <Icon
              as={
                isSidebarExpanded
                  ? TbLayoutSidebarLeftExpand
                  : TbLayoutSidebarRightExpand
              }
              boxSize={5}
              cursor="pointer"
              color="gray.800"
              flexShrink={0}
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
            />
          </Flex>
        </Flex>
        <Divider mb="4" />

        <Box>
          <Text fontSize="xl" fontWeight="bold">
            {report.title}
          </Text>
          {report.prompt && (
            <Text fontSize="sm" color="gray.600" mt="1">
              Description: {report.prompt}
            </Text>
          )}
          {parsedFilters && (
            <Flex flexWrap="wrap" gap="1" align="center">
              <CustomInsightsFilterSummary
                filters={parsedFilters}
                filled={false}
              />
            </Flex>
          )}
          <Divider mt="4" mb="6" />
        </Box>

        {parsedResult && (
          <Box>
            {parsedResult.charts?.map((chartData, index) => (
              <Box key={chartData.toolCallId || index} mb="4">
                <CopyableChart
                  variant="insightsReport"
                  data={{
                    title: chartData.title,
                    type: chartData.chart_type ?? "bar",
                    labels: chartData.labels,
                    values: chartData.values,
                    series: chartData.series,
                  }}
                />
              </Box>
            ))}
            {parsedResult.summary && (
              <Box mb="4">
                <CustomInsightsReportPrettyMarkdown>
                  {parsedResult.summary}
                </CustomInsightsReportPrettyMarkdown>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {isSidebarExpanded && (
        <ReportDetailSidebar
          title={report.title}
          authorName={report.creator?.fullName ?? "Unknown"}
          createdAt={report.createdAt}
          filters={parsedFilters}
          isCreator={isCreator}
          onSaveTitle={handleSaveTitle}
          onCopyToClipboard={handleCopyToClipboard}
          copying={copying}
          onNewExplorationFromReport={handleNewExplorationFromReport}
          onShare={onShareOpen}
          onDelete={onDeleteOpen}
        />
      )}

      {id && (
        <CustomInsightsReportShareModal
          reportId={id}
          creatorId={report.createdBy}
          currentUserId={currentUser.id}
          shares={report.shares}
          isOpen={isShareOpen}
          onClose={onShareClose}
        />
      )}

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Report
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete &quot;{report.title}&quot;? This
              action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter gap="2">
              <Button
                ref={cancelRef}
                size="sm"
                variant="outline"
                onClick={onDeleteClose}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="solid"
                colorScheme="red"
                isLoading={deleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

const SIDEBAR_ACTION_BUTTON_PROPS = {
  size: "sm",
  variant: "outline",
  minW: 0,
  h: "32px",
  minH: "32px",
  borderRadius: "6px",
  px: "12px",
  fontSize: "14px",
  fontWeight: "medium",
  color: "gray.800",
  borderColor: "gray.200",
} as const;

type ReportDetailSidebarProps = {
  title: string;
  authorName: string;
  createdAt: string;
  filters: SerializableFilterState | null;
  isCreator: boolean;
  onSaveTitle: (newTitle: string) => Promise<void>;
  onCopyToClipboard: () => void;
  copying: boolean;
  onNewExplorationFromReport: () => void;
  onShare: () => void;
  onDelete: () => void;
};

const ReportDetailSidebar: React.FC<ReportDetailSidebarProps> = ({
  title,
  authorName,
  createdAt,
  filters,
  isCreator,
  onSaveTitle,
  onCopyToClipboard,
  copying,
  onNewExplorationFromReport,
  onShare,
  onDelete,
}) => {
  const [editedTitle, setEditedTitle] = useState(title);

  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  return (
    <Flex
      flexDir="column"
      w="419px"
      minW="419px"
      h="100%"
      borderLeftWidth="1px"
      borderColor="gray.200"
      bg="gray.50"
      overflow="hidden"
      data-testid="report-detail-sidebar"
    >
      <Box flex={1} minH={0} overflow="auto">
        <Box px="32px" pt="24px" pb="32px">
          <Box mb="16px">
            <Text
              fontSize="12px"
              fontWeight="normal"
              lineHeight="16px"
              color="gray.600"
              mb="4px"
            >
              Report title
            </Text>
            {isCreator ? (
              <Input
                h="32px"
                px="10px"
                py="6px"
                fontSize="14px"
                fontWeight="normal"
                lineHeight="20px"
                borderRadius="6px"
                bg="white"
                borderColor="gray.200"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onBlur={() => {
                  const trimmed = editedTitle.trim();
                  if (trimmed && trimmed !== title) {
                    onSaveTitle(trimmed);
                  }
                }}
                data-testid="report-title-input"
              />
            ) : (
              <Text fontSize="14px" lineHeight="20px" color="gray.900">
                {title}
              </Text>
            )}
          </Box>

          <Box mb="16px">
            <Text
              fontSize="12px"
              fontWeight="normal"
              lineHeight="16px"
              color="gray.600"
              mb="4px"
            >
              Author
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.800">
              {authorName}
            </Text>
          </Box>

          <Box mb="16px">
            <Text
              fontSize="12px"
              fontWeight="normal"
              lineHeight="16px"
              color="gray.600"
              mb="4px"
            >
              Created date
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.800">
              {formatDate(createdAt)}
            </Text>
          </Box>

          {filters && (
            <Box>
              <Text
                fontSize="12px"
                fontWeight="normal"
                lineHeight="16px"
                color="gray.600"
                mb="4px"
              >
                Filters
              </Text>
              <Box pt="4px">
                <CustomInsightsFilterSummary filters={filters} filled={false} />
              </Box>
            </Box>
          )}
        </Box>

        <Box px="32px" py="8px">
          <Divider />
        </Box>

        <Box px="32px" pb="24px">
          <Flex align="center" gap="8px" mb="8px">
            <Icon as={HiOutlineClock} boxSize="16px" color="gray.600" />
            <Text
              fontSize="14px"
              lineHeight="20px"
              fontWeight="normal"
              color="gray.600"
            >
              History
            </Text>
          </Flex>
          <Text fontSize="12px" lineHeight="16px" color="gray.400">
            Report history will be available once persisted rerun history is
            implemented.
          </Text>
        </Box>

        <Box px="32px" py="8px">
          <Divider />
        </Box>

        <Box px="32px" pb="24px">
          <Flex flexWrap="wrap" gap="8px" align="stretch">
            <Button
              {...SIDEBAR_ACTION_BUTTON_PROPS}
              flex="1 0 calc(50% - 4px)"
              leftIcon={<Icon as={HiOutlineShare} boxSize={5} />}
              onClick={onShare}
            >
              Share
            </Button>
            <Button
              {...SIDEBAR_ACTION_BUTTON_PROPS}
              onClick={onCopyToClipboard}
              isLoading={copying}
              flex="1 0 calc(50% - 4px)"
              leftIcon={<Icon as={HiOutlineClipboard} boxSize={5} />}
            >
              Copy to clipboard
            </Button>
            <Button
              {...SIDEBAR_ACTION_BUTTON_PROPS}
              isDisabled
              flex="1 0 calc(50% - 4px)"
              leftIcon={<Icon as={HiOutlineArrowDownTray} boxSize={5} />}
            >
              Download as PDF
            </Button>
            <Button
              {...SIDEBAR_ACTION_BUTTON_PROPS}
              isDisabled
              flex="1 0 calc(50% - 4px)"
              leftIcon={<Icon as={HiOutlineArrowPath} boxSize={5} />}
            >
              Re-run report
            </Button>
            <Button
              {...SIDEBAR_ACTION_BUTTON_PROPS}
              onClick={onNewExplorationFromReport}
              flex="1 0 100%"
              leftIcon={<Icon as={HiOutlineGlobeAlt} boxSize={5} />}
            >
              New exploration from this report
            </Button>
          </Flex>
        </Box>

        {isCreator && (
          <>
            <Box px="32px" py="8px">
              <Divider />
            </Box>
            <Box px="32px" pb="24px">
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={onDelete}
              >
                Delete report
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Flex>
  );
};

export default CustomInsightsViewPage;
